<script setup>
import { onMounted, ref } from 'vue'
import {
  getConversionFunnel,
  getConversionFunnelAuthActivity,
  getConversionFunnelDailyClaimActivity,
  getConversionFunnelTrafficVideos,
  getConversionFunnelVisitors,
} from '../api/analytics.js'

const pageHost = ref(null)
const reports = new Map()

function isoStart(date) {
  return new Date(`${date}T00:00:00.000Z`).toISOString()
}

function isoEnd(date) {
  const end = new Date(`${date}T00:00:00.000Z`)
  end.setUTCDate(end.getUTCDate() + 1)
  return end.toISOString()
}

function reportFor(since, until, trafficScope) {
  const key = `${since}:${until}:${trafficScope}`
  if (!reports.has(key)) {
    reports.set(key, getConversionFunnel({
      since: isoStart(since),
      until: isoEnd(until),
      traffic_scope: trafficScope,
    }))
  }
  return reports.get(key)
}

function responsePayload(pathname, report) {
  if (pathname === '/api/traffic') {
    return {
      platforms: report.traffic.platforms.map(platform => ({ ...platform, totalViews: platform.views })),
      socialVisits: report.traffic.social_visits,
      emailCampaigns: report.traffic.email_campaigns,
    }
  }
  if (pathname === '/api/website-visits') {
    return {
      pageViews: report.website.views,
      uniqueVisitors: report.website.users,
      pages: report.website.pages,
      locations: report.website.locations,
      devices: report.website.devices,
      sources: report.website.sources,
    }
  }
  if (pathname === '/api/registrations') {
    return {
      registration: {
        total: report.registrations.views,
        users: report.registrations.users,
        methods: report.registrations.methods,
      },
      login: {
        total: report.registrations.login.views,
        users: report.registrations.login.users,
        methods: report.registrations.login.methods,
      },
    }
  }
  if (pathname === '/api/website-usage') {
    return {
      textInputs: report.usage.text_input.views,
      textInputUsers: report.usage.text_input.users,
      voiceInputs: report.usage.voice_input.views,
      voiceInputUsers: report.usage.voice_input.users,
      sliceVideos: report.usage.slice_video.views,
      sliceVideoUsers: report.usage.slice_video.users,
      globalVideos: report.usage.global_video.views,
      globalVideoUsers: report.usage.global_video.users,
      freeGames: report.usage.free_game.views,
      freeGameUsers: report.usage.free_game.users,
    }
  }
  if (pathname === '/api/point-selection') {
    return {
      dailyClaims: report.points.daily_claim.views,
      dailyClaimUsers: report.points.daily_claim.users,
      purchasePageViews: report.points.purchase_page.views,
      purchasePageUsers: report.points.purchase_page.users,
    }
  }
  if (pathname === '/api/paypoints') {
    return {
      rewriteViews: report.paypoints.rewrite.views,
      rewriteUsers: report.paypoints.rewrite.users,
      videoViews: report.paypoints.video_generation.views,
      videoUsers: report.paypoints.video_generation.users,
    }
  }
  if (pathname === '/api/remaining-metrics') {
    return {
      orders: report.orders.views,
      orderUsers: report.orders.users,
      paymentFailures: report.payment_failure.views,
      paymentFailureUsers: report.payment_failure.users,
      paymentSuccesses: report.payment_success.views,
      paymentSuccessUsers: report.payment_success.users,
      shareClicks: report.sharing.total.views,
      shareUsers: report.sharing.total.users,
    }
  }
  throw new Error(`Unsupported conversion funnel request: ${pathname}`)
}

async function funnelFetch(url) {
  const target = new URL(url, window.location.origin)
  if (target.pathname === '/api/behavior-visitors') {
    const visitors = await getConversionFunnelVisitors({
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
    })
    return { ok: true, json: async () => visitors }
  }
  if (target.pathname === '/api/traffic-videos') {
    const videos = await getConversionFunnelTrafficVideos({
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
      traffic_scope: target.searchParams.get('traffic_scope') || 'range',
      platform: target.searchParams.get('platform'),
    })
    return { ok: true, json: async () => videos }
  }
  if (target.pathname === '/api/auth-activity') {
    const activity = await getConversionFunnelAuthActivity({
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
    })
    return { ok: true, json: async () => activity }
  }
  if (target.pathname === '/api/daily-claim-activity') {
    const activity = await getConversionFunnelDailyClaimActivity({
      since: isoStart(target.searchParams.get('since')),
      until: isoEnd(target.searchParams.get('until')),
    })
    return { ok: true, json: async () => activity }
  }
  const report = await reportFor(
    target.searchParams.get('since'),
    target.searchParams.get('until'),
    target.searchParams.get('traffic_scope') || 'range',
  )
  return { ok: true, json: async () => responsePayload(target.pathname, report) }
}

function loadScript(src) {
  const existing = document.querySelector(`script[src="${src}"]`)
  if (existing) {
    if (window.THREE) return Promise.resolve()
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve, { once: true })
      existing.addEventListener('error', reject, { once: true })
    })
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.addEventListener('load', resolve, { once: true })
    script.addEventListener('error', reject, { once: true })
    document.head.appendChild(script)
  })
}

function scopedDocument(root, host) {
  return {
    documentElement: host,
    getElementById: id => root.getElementById(id),
    querySelector: selector => root.querySelector(selector),
    createElement: tag => document.createElement(tag),
    addEventListener: (...args) => root.addEventListener(...args),
  }
}

function themeBridge() {
  return `
    :host {
      display: block;
      color-scheme: inherit;
      --page: var(--drama-color-bg);
      --surface: var(--drama-color-surface);
      --ink: var(--drama-color-text);
      --ink-2: var(--drama-color-text-muted);
      --muted: var(--drama-color-text-muted);
      --grid: var(--drama-color-surface-alt);
      --border: var(--drama-color-border);
      --good: var(--drama-color-success);
      --critical: var(--drama-color-danger);
      --accent: var(--drama-color-primary);
      --spout: var(--drama-color-primary);
      --alert-red: var(--drama-color-danger);
      --alert-yellow: var(--drama-color-warning);
      --alert-blue: var(--drama-color-info);
      --alert-green: var(--drama-color-success);
      --shadow: var(--drama-shadow-sm);
      background: var(--drama-color-bg);
    }
  `
}

onMounted(async () => {
  const response = await window.fetch('/conversion-funnel.html')
  if (!response.ok) throw new Error(`Failed to load conversion funnel: ${response.status}`)

  const source = await response.text()
  const parsed = new DOMParser().parseFromString(source, 'text/html')
  const mainScript = [...parsed.scripts].at(-1)?.textContent
  const content = parsed.querySelector('.wrap')
  const css = parsed.querySelector('style')?.textContent
  if (!pageHost.value || !mainScript || !content || !css) throw new Error('Invalid conversion funnel document')

  const root = pageHost.value.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = `${css}\n${themeBridge()}`
  root.append(style, content.cloneNode(true))

  await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js')
  await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js')

  const run = new Function('document', 'window', 'fetch', 'THREE', 'ResizeObserver', 'MutationObserver', 'requestAnimationFrame', mainScript)
  run(
    scopedDocument(root, pageHost.value),
    window,
    funnelFetch,
    window.THREE,
    ResizeObserver,
    MutationObserver,
    requestAnimationFrame,
  )
})
</script>

<template>
  <div ref="pageHost" class="conversion-funnel-page" />
</template>

<style scoped>
.conversion-funnel-page {
  min-width: 0;
  background: var(--drama-color-bg);
}
</style>
